import {
  LAMPORTS_PER_SOL,
  type Connection,
  type PublicKey
} from '@solana/web3.js'
import { walletBalancesVar } from 'shared/store/balance-store'
import { openWalletDeposit } from 'shared/store/wallet-deposit'

/** Below this a wallet cannot pay even a few signature fees. */
export const MIN_SOL_FOR_FEE = 0.0001

/**
 * What the Deposit dialog asks to keep: the fees of many bets plus the rent
 * of the token accounts a first bet opens.
 */
export const RECOMMENDED_SOL_BALANCE = 0.01

export const NOT_ENOUGH_SOL_MESSAGE =
  'Not enough SOL for the Solana network fee. Add some SOL to your wallet (0.01 SOL covers many bets) and try again.'

export class NotEnoughSolError extends Error {
  /** The RPC error this one replaced, if any. */
  readonly reason?: unknown

  constructor(reason?: unknown) {
    super(NOT_ENOUGH_SOL_MESSAGE)
    this.name = 'NotEnoughSolError'
    this.reason = reason
  }
}

// NOTE: what the RPC and the System program report when the fee payer cannot
// pay the fee or the rent of an account the transaction opens
const NOT_ENOUGH_SOL_PATTERNS = [
  /no record of a prior credit/i,
  /insufficient lamports/i,
  /insufficient funds for (fee|rent)/i,
  /InsufficientFundsFor(Fee|Rent)/
]

const getErrorText = (error: unknown): string => {
  if (!(error instanceof Error)) return String(error)

  // NOTE: `SendTransactionError` keeps the simulation logs apart from the message
  const { logs } = error as { logs?: unknown }
  return Array.isArray(logs) ?
      [error.message, ...logs].join('\n')
    : error.message
}

export const isNotEnoughSolError = (error: unknown): boolean =>
  error instanceof NotEnoughSolError ||
  NOT_ENOUGH_SOL_PATTERNS.some(pattern => pattern.test(getErrorText(error)))

/**
 * Replaces the RPC's "no SOL" failures with one message the game toasts can
 * show as is, and opens the Deposit dialog for the paying wallet. Any other
 * error is returned unchanged.
 */
export const explainNotEnoughSol = (
  error: unknown,
  payerAddress: string | null
): unknown => {
  if (!isNotEnoughSolError(error)) return error

  if (payerAddress) openWalletDeposit(payerAddress)
  return error instanceof NotEnoughSolError ? error : (
      new NotEnoughSolError(error)
    )
}

const readLamports = async (
  connection: Connection,
  publicKey: PublicKey
): Promise<number | null> => {
  try {
    return await connection.getBalance(publicKey, 'confirmed')
  } catch {
    return null
  }
}

/**
 * Stops a game transaction before the wallet signs it when the signer has no
 * SOL for the network fee. The backend balance answers first; only a low or
 * unknown one costs an RPC call, so a fresh top-up the backend has not seen
 * yet is never refused. An RPC failure lets the transaction through.
 */
export const assertSolForFee = async (
  connection: Connection,
  publicKey: PublicKey
): Promise<void> => {
  const address = publicKey.toBase58()
  const knownBalance = walletBalancesVar()[address]?.nativeBalance
  if (knownBalance !== undefined && knownBalance >= MIN_SOL_FOR_FEE) return

  const lamports = await readLamports(connection, publicKey)
  if (lamports === null || lamports >= MIN_SOL_FOR_FEE * LAMPORTS_PER_SOL) {
    return
  }

  openWalletDeposit(address)
  throw new NotEnoughSolError()
}
